import orderApi, {
    InvisiblePlatformOrderResult,
    OrderListCount,
    QueryOrderListResult,
    batchGetShopOrderCount,
    getInVisiblePlatformOrders,
    getOrderListCount,
} from "@/services/api/order";
import { getUndeliverableAreaParams } from "@/services/undeliverableAreaService";
import ShippingAreaStore from "@/store/ShippingAreaStore";
import { concurrentQueue, uuid } from "@/utils";
import { observable, when, makeObservable, actionBound } from "mobx";
import { createContext, useContext } from "react";
import OrderTab from "../enum/OrderTab";
import OrderManagementTableStore from "./OrderManagementTableStore";
import type GlobalStore from "@/store";
import { OrderTagSearchType } from "../components/OrderTable/SearchPane/OrderTagSelectField";
import { showShopExpiredModal } from "@/services/http/utils";
import TableSearchState from "../redux/states/TableSearchState";
import OrderTableStorageStore from "./OrderTableStorageStore";
import { OrderQuickSearchType } from "../components/OrderTable/SearchPane/QuickField";
import { splitRegExp } from "@/utils/RegExp";
import { getKeyInfo } from "../components/OrderTable/SearchPane/LinkedProductSearchFields/utils";
import { getAffiliateMarketerIdsByShop } from "../components/OrderTable/SearchPane/AffiliateMarketerField/utils";
import OrderSyncStore from "./OrderSyncStore";
import SupplierPartnershipsStore from "@/store/SupplierPartnershipsStore";
import RetailerPartnershipsStore from "@/store/RetailerPartnershipsStore";
import OrderLogisticsWarningStore from "@/store/OrderLogisticsWarningStore";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import OrderStatusNameMap from "@/map/OrderStatusNameMap";
import OrderTag from "@/enum/OrderTag";
import { cloneDeep, uniqBy } from "lodash";
import InstantShipStore from "./InstantShipStore";

export const OrderManagementContext = createContext<OrderManagementStore>(null);

export function useOrderManagementStore() {
    return useContext(OrderManagementContext) as OrderManagementStore;
}

/**
 * 将联动字段的skuKey转化成skuInfoId
 */
function transformLinkFields(params) {
    if (params.skuPropertiesFieldSkuKeys) {
        params.skuPropertiesSkuInfoIds = params.skuPropertiesFieldSkuKeys.map((key) => {
            const [, skuId] = getKeyInfo(key);

            return Number(skuId);
        });

        delete params.skuPropertiesFieldSkuKeys;
    }

    if (params.titleFieldSkuKeys) {
        params.titleSkuInfoIds = params.titleFieldSkuKeys.map((key) => {
            const [, skuId] = getKeyInfo(key);

            return Number(skuId);
        });

        delete params.titleFieldSkuKeys;
    }

    if (params.externalIdFieldSkuKeys) {
        params.externalIdSkuInfoIds = params.externalIdFieldSkuKeys.map((key) => {
            const [, skuId] = getKeyInfo(key);

            return Number(skuId);
        });

        delete params.externalIdFieldSkuKeys;
    }

    return params;
}

function transformAffiliateMarketerFields(params, shopId: number) {
    if (params.affiliateMarketerKeys) {
        const affiliateMarketerIds = getAffiliateMarketerIdsByShop(params.affiliateMarketerKeys, shopId);

        params.affiliateMarketerIds = affiliateMarketerIds.length ? affiliateMarketerIds : [-1];

        delete params.affiliateMarketerKeys;
    }

    return params;
}

/**
 * 构建请求订单列表的参数，表单里有特殊的参数，需要在发送前转换
 */
export function buildListOrdersParams(searchFromValues: TableSearchState) {
    const {
        originalIds: rawOriginalIds,
        skuOriginalIds: rawSkuOriginalIds,
        productOriginalIds: rawProductOriginalIds,
        shippingAreaId: rawShippingAreaId,
        markIds: rawMarkIds,
        minWeight: rawMinWeight,
        maxWeight: rawMaxWeight,
        retailerShopIds,
        undeliverableArea: rawUndeliverableArea,
        waybillNo: rawWaybillNo,
        itemQuantityTo: rawItemQuantityTo,
        itemQuantityFrom: rawItemQuantityFrom,
        supplierShopId,
        quicks,
        orderTagSearchType,
        skuProperties,
        orderItemTitle,
        orderItemExternalId,
        fromOrigin,
        orderItemTitleValue,
        orderItemExternalIdValue,
        skuPropertiesValue,
        orderQuickSearchType,
        quickTimeSelectConfigId, // 此参数仅页面展示，不用传后端
        printBatchNo,
        orderTags: rawOrderTags,
        excludedOrderTags: rawExcludedOrderTags,
        ...search
    } = searchFromValues;

    let orderTags: OrderTag[];
    let excludedOrderTags: OrderTag[];

    if (rawOrderTags?.length) {
        orderTags = cloneDeep(rawOrderTags);
        if (rawOrderTags?.includes(OrderTag.CANNOT_SHIP)) {
            orderTags.push(OrderTag.TAOTIAN_PAID_FORBIN_CONSIGN, OrderTag.ALI_CAN_NOT_SHIP);
        }
    }

    if (rawExcludedOrderTags?.length) {
        excludedOrderTags = cloneDeep(rawExcludedOrderTags);
        if (rawExcludedOrderTags?.includes(OrderTag.CANNOT_SHIP)) {
            excludedOrderTags.push(OrderTag.TAOTIAN_PAID_FORBIN_CONSIGN, OrderTag.ALI_CAN_NOT_SHIP);
        }
    }

    let originalIds;
    let skuOriginalIds;
    let productOriginalIds;

    if (rawOriginalIds) {
        originalIds = rawOriginalIds?.split(splitRegExp)?.filter((it) => it);
    }

    if (rawSkuOriginalIds) {
        skuOriginalIds = rawSkuOriginalIds?.split(splitRegExp)?.filter((it) => it);
    }

    if (rawProductOriginalIds) {
        productOriginalIds = rawProductOriginalIds?.split(splitRegExp)?.filter((it) => it);
    }

    const markIds = rawMarkIds?.join();

    const shippingAreaId = rawShippingAreaId === "setting" ? undefined : rawShippingAreaId;

    let shippingArea: {
        excludedCityCodes: number[];
        excludedDistrictCodes: number[];
        includedProvinceCodes: number[];
    };

    if (shippingAreaId) {
        const findShippingArea = ShippingAreaStore?.shippingAreaMap?.get(shippingAreaId as number);

        if (findShippingArea) {
            const excludedCityCodes = findShippingArea?.excludedCityCodes
                ?.split(",")
                ?.filter((it) => !!it)
                .map(Number);

            const excludedDistrictCodes = findShippingArea?.excludedDistrictCodes
                ?.split(",")
                ?.filter((it) => !!it)
                .map(Number);

            const includedProvinceCodes = findShippingArea?.includedProvinceCodes
                ?.split(",")
                ?.filter((it) => !!it)
                .map(Number);

            shippingArea = {
                excludedCityCodes: excludedCityCodes,
                excludedDistrictCodes: excludedDistrictCodes,
                includedProvinceCodes: includedProvinceCodes,
            };
        }
    }

    let minWeight = Number(rawMinWeight);
    if (!Number.isNaN(minWeight) && rawMinWeight !== "") {
        minWeight = minWeight * 1000;
    } else {
        minWeight = undefined;
    }

    let maxWeight = Number(rawMaxWeight);
    if (!Number.isNaN(maxWeight) && rawMaxWeight !== "") {
        maxWeight = maxWeight * 1000;
    } else {
        maxWeight = undefined;
    }

    let itemQuantityTo = undefined;
    if (rawItemQuantityTo) {
        itemQuantityTo = Number.parseFloat(rawItemQuantityTo);
        if (Number.isNaN(itemQuantityTo)) {
            itemQuantityTo = undefined;
        }
    }

    let itemQuantityFrom = undefined;
    if (rawItemQuantityFrom) {
        itemQuantityFrom = Number.parseFloat(rawItemQuantityFrom);
        if (Number.isNaN(itemQuantityFrom)) {
            itemQuantityFrom = undefined;
        }
    }

    let retailerDropShippingInfoParams;

    if (retailerShopIds) {
        retailerDropShippingInfoParams = { retailerShopIds };
    }

    /**
     * 内部有使用到OrderSettingStore 考虑重构
     */
    const undeliverableArea = getUndeliverableAreaParams(rawUndeliverableArea);
    let supplierDropShippingInfoParams;

    if (supplierShopId) {
        supplierDropShippingInfoParams = { supplierShopId };
    }

    let waybillNos;

    if (rawWaybillNo) {
        waybillNos = rawWaybillNo?.split(splitRegExp)?.filter((it) => it);
    }

    let includeAffiliateMarketer;
    let affiliateMarketerKeys;
    if (fromOrigin) {
        includeAffiliateMarketer = fromOrigin?.[0];

        if (fromOrigin?.[1]?.length) {
            affiliateMarketerKeys = fromOrigin?.[1];
        }
    }

    let printBatchNos;
    if (printBatchNo) {
        printBatchNos = printBatchNo.split(splitRegExp).filter((it) => !!it);
    }

    const params: any = {
        ...search,
        orderTags,
        excludedOrderTags,
        itemQuantityTo,
        itemQuantityFrom,
        originalIds,
        skuOriginalIds,
        productOriginalIds,
        shippingArea,
        markIds,
        minWeight,
        maxWeight,
        retailerDropShippingInfoParams,
        waybillNos,
        undeliverableArea,
        supplierDropShippingInfoParams,
        includeAffiliateMarketer,
        affiliateMarketerKeys,
        printBatchNos,
    };

    /** 优先从联动组件中获取值 */
    params.orderItemTitle = orderItemTitleValue?.orderItemTitle;
    params.skuProperties = skuPropertiesValue?.skuProperties;
    params.orderItemExternalId = orderItemExternalIdValue?.orderItemExternalId;

    /** 跨平台中使用， 注意，需要在请求前再次转化成最终的SkuInfoIds */
    if (orderItemTitleValue?.titleFieldSkuKeys) {
        delete params.orderItemTitle;

        params.titleFieldSkuKeys = orderItemTitleValue?.titleFieldSkuKeys;
    }

    if (skuPropertiesValue?.skuPropertiesFieldSkuKeys) {
        delete params.skuProperties;

        params.skuPropertiesFieldSkuKeys = skuPropertiesValue.skuPropertiesFieldSkuKeys;
    }

    if (orderItemExternalIdValue?.externalIdFieldSkuKeys) {
        delete params.orderItemExternalId;

        params.externalIdFieldSkuKeys = orderItemExternalIdValue.externalIdFieldSkuKeys;
    }

    if (orderTagSearchType === OrderTagSearchType.UnInclude) {
        const orderTags = params.orderTags;
        params.excludedOrderTags = orderTags;

        params.orderTags = undefined;
    }

    if (quicks) {
        if (orderQuickSearchType === OrderQuickSearchType.AllMatchQuickCriterion) {
            params.allMatchQuickCriterionList = quicks;
        } else {
            params.anyMatchQuickCriterionList = quicks;
        }

        delete params.quicks;
    }

    return params;
}

interface GetOneShopDataByOffsetPaginationByConcurrentProps<T> {
    totalCount: number;
    pageNo: number;
    pageSize: number;
    maxLimit: number;
    onLoadShopData(offset: number, limit: number): Promise<T[]>;
}

/**
 * 单店铺并发模式获取订单列表
 */
export async function getOneShopDataByOffsetPaginationByConcurrent<T>(
    props: GetOneShopDataByOffsetPaginationByConcurrentProps<T>,
) {
    const { totalCount, pageNo, pageSize, maxLimit = 200, onLoadShopData } = props;

    const needLoadShopOrdersInfos: {
        offset: number;
        limit: number;
        dataSource: T[];
    }[] = [];

    const offset = pageSize * (pageNo - 1);

    const orderCountRemainder = totalCount - offset;

    if (orderCountRemainder <= 0) {
        [];
    }

    // 当前当铺实际应该需要加载的订单数量
    const currentShopOrderShouldLoadCount = Math.min(orderCountRemainder, pageSize);

    const limit = Math.min(currentShopOrderShouldLoadCount, maxLimit);

    // 当前店铺根据limit计算需要加载几次
    const count = Math.floor(currentShopOrderShouldLoadCount / limit);

    // 加载完后，还剩多少
    const remainder = currentShopOrderShouldLoadCount % limit;

    let startOffset = offset;
    for (let i = 0; i < count; i++) {
        needLoadShopOrdersInfos.push({
            offset: startOffset,
            limit: limit,
            dataSource: [],
        });

        startOffset += limit;
    }

    if (remainder) {
        needLoadShopOrdersInfos.push({
            offset: startOffset,
            limit: remainder,
            dataSource: [],
        });

        startOffset += remainder;
    }

    const items: T[] = [];
    await concurrentQueue({
        data: needLoadShopOrdersInfos,
        maxTask: 5,
        async callback(orderInfo) {
            const { limit, offset } = orderInfo;

            const list = await onLoadShopData(offset, limit);

            orderInfo.dataSource = list;
        },
    });

    for (let i = 0; i < needLoadShopOrdersInfos.length; i++) {
        const ordersInfo = needLoadShopOrdersInfos[i];

        items.push(...ordersInfo.dataSource);
    }

    return items;
}

/**
 * 订单列表状态管理类
 */
export default class OrderManagementStore {
    constructor(public globalStore: typeof GlobalStore) {
        makeObservable(this, {
            searchFormValues: observable,
            shopId: observable,
            tab: observable,
            setTab: actionBound,
            orderListCount: observable,
            getOrderListCount: actionBound,
            collectData: actionBound,
            fetchOrders: actionBound,
            invisiblePlatformOrderResult: observable,
        });
        this.table = new OrderManagementTableStore();
        this.storage = new OrderTableStorageStore(globalStore);
        this.orderSyncStore = new OrderSyncStore();
        this.orderLogisticsWarningStore = new OrderLogisticsWarningStore(globalStore);
        this.instantShipStore = new InstantShipStore();
    }

    table: OrderManagementTableStore;

    storage: OrderTableStorageStore;

    orderSyncStore: OrderSyncStore;

    orderLogisticsWarningStore: OrderLogisticsWarningStore;

    instantShipStore: InstantShipStore;

    supplierPartnershipsStore = new SupplierPartnershipsStore();

    retailerPartnershipsStore = new RetailerPartnershipsStore();

    lastRequestId = null;

    searchFormValues: any = {};

    shopId: number;

    tab: OrderTab;

    setTab(tab: OrderTab) {
        this.tab = tab;
    }

    orderListCount?: OrderListCount;

    async getOrderListCount(shopId?: number, tab?: OrderTab) {
        /**
         * @todo 移除设置 shopId 和 tab 的逻辑
         * shopId:进入店铺即设置
         * tab:进入Tab即设置
         */

        if (shopId) {
            this.shopId = shopId;
        }

        if (tab) {
            this.tab = tab;
        }

        if (!(this.shopId && this.tab)) {
            this.orderListCount = undefined;

            return;
        }

        let requestId = uuid();

        this.lastRequestId = requestId;

        const orderListCount = await getOrderListCount({
            shopId: this.shopId,
            tab: this.tab,
        });

        orderListCount.shopId = shopId;
        orderListCount.tab = this.tab;

        if (this.lastRequestId === requestId) {
            this.orderListCount = orderListCount;
        }

        return orderListCount;
    }

    fetchOrderId = 0;

    collectData(params) {
        try {
            if (params.printBatchNos) {
                collectEvent(DataTrackEvent.PRINT_BATCH_NUMBER_SEARCH, {});
            }

            if (params.itemStatuses?.length) {
                collectEvent(DataTrackEvent.ORDER_ITEM_STATUS_SEARCH, {
                    content: params.itemStatuses.map((it) => OrderStatusNameMap[it]).join(),
                });
            }
        } catch {}
    }

    async fetchOrders(shopId: number, tab?: OrderTab) {
        console.time("all fetch order");
        this.table.loading = true;

        const fetchOrderId = ++this.fetchOrderId;

        try {
            const { isExpired } = await this.globalStore.checkManualShopExpired(shopId);

            if (isExpired) {
                showShopExpiredModal({ shopId });

                this.table.setOrders([]);
                return;
            }

            if (!tab) {
                tab = this.tab || OrderTab.PENDING_SHIPMENT;
            }

            this.tab = tab;

            // 重新获取当前店铺的本地配置
            await this.table.getOrderTableConfig(shopId, tab);

            if (!this.table.pagination.pageNo) {
                this.table.pagination.pageNo = 1;
            }

            const pageNo = this.table.pagination.pageNo;
            const pageSize = this.table.pagination.pageSize;
            const orderSort = this.table.listOptions.orderSort;
            const fulfillmentType = this.table.listOptions.fulfillmentType;
            // 然后拿取本地配置 请求数据

            let params = await buildListOrdersParams(this.table.form.getFieldsValue());

            params = transformLinkFields(params);

            params.shopId = shopId;
            params.tab = tab;
            params.orderSort = orderSort;
            params.fulfillmentType = fulfillmentType;
            params = transformAffiliateMarketerFields(params, shopId);

            this.collectData(params);

            this.table.prevQueryParams = JSON.stringify(params);

            console.time("load orders");

            let dataSource: QueryOrderListResult = {
                orders: [],
                totalCount: 0,
            };

            if (pageSize <= 1000) {
                dataSource = await orderApi.list({
                    ...params,
                    pageNo,
                    pageSize,
                });
            } else {
                const result = await batchGetShopOrderCount({
                    ...params,
                    shopIds: [shopId],
                });

                const totalCount = result?.[0]?.totalOrders || 0;

                const orders = await getOneShopDataByOffsetPaginationByConcurrent({
                    totalCount,
                    pageNo,
                    pageSize,
                    maxLimit: 200,
                    onLoadShopData: async (offset: number, limit: number) => {
                        const result = await orderApi.list({
                            ...params,
                            needTotalCount: false,
                            offset,
                            limit,
                        });

                        return result.orders;
                    },
                });

                dataSource.totalCount = totalCount;
                dataSource.orders = uniqBy(orders, "id");
            }

            console.timeEnd("load orders");

            if (fetchOrderId !== this.fetchOrderId) {
                return;
            }

            this.table.pagination.totalCount = dataSource.totalCount;

            console.time("set orders");

            this.table.setOrders(dataSource.orders);

            console.timeEnd("set orders");
        } catch (error) {
            console.error(error);

            this.table.setOrders([]);
        } finally {
            console.timeEnd("all fetch order");

            if (fetchOrderId !== this.fetchOrderId) {
                return;
            }

            this.table.loading = false;

            console.time("set lock orders");

            await this.table.orderLockStore.handleLockOrders(this.table.orders);

            console.timeEnd("set lock orders");
        }

        this.table.loadManualMergeableOrderCount(this.table.orders, this.tab);

        this.loadInVisiblePlatformOrderInfo(shopId, this.tab, fetchOrderId);
    }

    invisiblePlatformOrderResult: InvisiblePlatformOrderResult = null;

    async loadInVisiblePlatformOrderInfo(shopId: number, tab: OrderTab, fetchOrderId: number) {
        if ([OrderTab.ON_HOLD, OrderTab.SHIPPED_REFUNDING, OrderTab.PENDING_SHIPMENT_REFUNDING].includes(tab)) {
            /** 这里直接使用when简单处理，没有使用完整的事件驱动（即加载完订单和加载完数量之后进行一次ensureInfo的加载） */
            await when(
                () => !!this.orderListCount && this.orderListCount.shopId === shopId && this.orderListCount.tab === tab,
            );

            if (this.table.orders?.length === 0) {
                if (
                    (tab === OrderTab.ON_HOLD && this.orderListCount.totalOnHoldOrders) ||
                    (tab === OrderTab.PENDING_SHIPMENT_REFUNDING &&
                        this.orderListCount.totalPendingShipmentRefundingOrders) ||
                    (tab === OrderTab.SHIPPED_REFUNDING && this.orderListCount.totalShippedRefundingOrders)
                ) {
                    const ret = await getInVisiblePlatformOrders(shopId, tab);

                    if (this.fetchOrderId === fetchOrderId) {
                        ret.shopId = shopId;
                        ret.tab = tab;

                        this.invisiblePlatformOrderResult = ret;

                        return;
                    }
                }
            }
        }

        if (this.invisiblePlatformOrderResult) {
            this.invisiblePlatformOrderResult = null;
        }
    }
}
